import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';

export default function PermitQuestionPage() {
  const pageData = {
    title: "Do You Need a Permit for Impact Windows in South Florida?",
    metaDescription: "Yes — all impact window installations in South Florida require a permit. See requirements, timelines, and how we handle the entire process for you.",
    canonicalPath: "/permits-impact-windows-miami-dade",
    
    directAnswer: (
      <>
        <p className="mb-3">
          <strong>Yes — all impact window installations in South Florida require a building permit</strong> in accordance with the Florida Building Code, regardless of project size.
        </p>
        <p className="mb-3">
          We handle the entire permitting process for you. Our permit processing fee is $500, which covers plan preparation, permit package assembly, submission, and HOA coordination (if applicable). City permit fees ($150–$1,000+) are separate and vary by municipality.
        </p>
        <p>
          Permit timelines vary depending on the municipality. We coordinate everything so you don't have to deal with the city or your HOA.
        </p>
      </>
    ),

    sections: [
      {
        title: "Why Permits Are Required",
        testId: "why-permits",
        content: (
          <p>
            South Florida municipalities require permits for impact windows to ensure:
          </p>
        ),
        bullets: [
          "Installations meet current Florida Building Code",
          "Products meet HVHZ (High-Velocity Hurricane Zone) standards",
          "Work is performed by licensed contractors",
          "Your insurance coverage remains valid for hurricane damage",
          "Future home sales aren't delayed by unpermitted work"
        ],
        highlight: "Skipping permits can void your insurance and create issues when selling your home."
      },

      {
        title: "What is Required for Permit Approval?",
        testId: "permit-requirements",
        content: (
          <>
            <p className="mb-3">
              Permit requirements vary depending on the municipality, but most projects require a combination of the following:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1 text-sm">
              <li>Completed permit application</li>
              <li>Product approvals (NOAs) confirming compliance with Florida Building Code / HVHZ</li>
              <li>Floor plans indicating window and door locations</li>
              <li>Window and door schedule</li>
              <li>HOA approval and/or HOA attestations (if applicable)</li>
              <li>Wind load calculations (required for certain projects)</li>
              <li>Property records (such as warranty deed or property appraiser information)</li>
              <li>Business documentation if the property is owned by an LLC (Sunbiz registration)</li>
              <li>Payment of city permit fees</li>
            </ul>
            <p className="text-sm">
              We prepare and submit all required documentation and coordinate directly with the building department to move the process forward. Additional documents may be requested by the city depending on the scope of work.
            </p>
          </>
        )
      },

      {
        title: "What the Permit Process Involves",
        testId: "permit-process",
        bullets: [
          "Application submission with product NOAs and installation plans",
          "Plan review by local building department (timelines vary by municipality)",
          "Permit processing fee ($500) + City permit fees ($150–$1,000+)",
          "Professional installation by licensed contractor",
          "Final inspection by municipal inspector",
          "Permit closure and certificate of completion"
        ]
      },

      {
        title: "How Long Does It Take?",
        testId: "permit-timeline",
        content: (
          <>
            <p className="mb-3">
              Permit timelines vary depending on the municipality:
            </p>
          </>
        ),
        bullets: [
          "Permit application to approval: 3–5 days to 2–4 weeks (varies by city)",
          "Installation: 1–3 days (after approval)",
          "Final inspection scheduling: Few days to 1 week (depending on city availability)",
          "Total from start to completion: Varies by municipality"
        ],
        highlight: "We coordinate every step so you don't have to visit the building department or schedule inspections. We'll provide specific timelines for your area when you request a quote."
      },

      {
        title: "What We Handle For You",
        testId: "what-we-handle",
        bullets: [
          "Complete permit application and submission ($500 processing fee)",
          "Product NOA certification and documentation",
          "Installation plan preparation",
          "Building department coordination and communication",
          "City permit fee collection and payment ($150–$1,000+)",
          "HOA coordination and document submission (included in processing fee)",
          "Engineering coordination if required",
          "Final inspection scheduling and coordination",
          "Certificate of completion delivery"
        ]
      },

      {
        title: "HOA Approval Process",
        testId: "hoa-approval",
        content: (
          <p>
            If your property has an HOA, approval is often required <strong>before we can proceed with permitting or ordering materials.</strong> We handle this entire process for you at no additional charge (included in the $500 permit processing fee):
          </p>
        ),
        bullets: [
          "HOA approval typically adds 2–4 weeks to timeline depending on association review process",
          "We submit all required documentation: contractor license and insurance, scope of work, product approvals, and supporting documents",
          "Common HOA requirements: exterior color matching, grid patterns, architectural compatibility",
          "Tip: Start HOA process early — we can provide documentation before quote acceptance to save time"
        ]
      }
    ],

    relatedLinks: [
      {
        title: "Complete Permit Guide",
        description: "Detailed permit process walkthrough",
        url: "/impact-window-permits-miami-dade"
      },
      {
        title: "Impact Window Cost",
        description: "See pricing including permit fees",
        url: "/impact-window-cost-miami"
      },
      {
        title: "Installation Process",
        description: "Full project timeline explained",
        url: "/impact-window-installation-process"
      }
    ],

    ctaText: "We Handle All Permits",
    ctaSubtext: "Get your quote and we'll manage the entire permit process",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Permit Requirements" }
    ]
  };

  return <ContentPageTemplate {...pageData} />;
}
